import { useEffect, useState } from 'react';
import { KeyRound, Lightbulb, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { ApiError } from '../../lib/api';
import { adminApi, isLoggedIn } from '../../lib/admin';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoggedIn()) navigate('/admin/objednavky', { replace: true });
  }, [navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || pending) return;
    setPending(true);
    setError(null);
    try {
      await adminApi.login(password);
      toast.success('Přihlášeno');
      navigate('/admin/objednavky', { replace: true });
    } catch (err) {
      const message =
        (err as ApiError)?.status === 503
          ? 'Administrace zatím není zapnutá (chybí heslo správce v nastavení serveru).'
          : ((err as ApiError)?.message ?? 'Přihlášení se nezdařilo.');
      setError(message);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="grid-paper flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <span className="board grid h-14 w-14 place-items-center rounded-2xl">
            <Lightbulb className="h-7 w-7 text-accent" strokeWidth={2} />
          </span>
          <div>
            <h1 className="font-serif text-2xl">Správa Kabinetu</h1>
            <p className="mt-1 text-sm text-ink-soft">Přihlášení pro prodejkyně</p>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="rounded-card border border-line bg-card p-5 shadow-card"
        >
          <label htmlFor="admin-password" className="text-sm font-semibold text-ink">
            Heslo
          </label>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-line bg-paper px-3 focus-within:border-board">
            <KeyRound className="h-4 w-4 flex-none text-ink-soft" strokeWidth={2} />
            <input
              id="admin-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 w-full bg-transparent text-ink outline-none placeholder:text-ink-soft"
              placeholder="Zadejte heslo"
              autoFocus
            />
          </div>

          {error && (
            <p className="mt-3 rounded-lg border border-error/30 bg-error/5 px-3 py-2 text-sm text-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={pending || !password}
            className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-board font-semibold text-chalk transition hover:bg-board-deep disabled:opacity-50"
          >
            <Lock className="h-4 w-4" strokeWidth={2} />
            {pending ? 'Přihlašuji…' : 'Přihlásit se'}
          </button>
        </form>
      </div>
    </div>
  );
}
